import { getSupabaseClient } from '@/integrations/supabase/client';
import type { DispatchPrintTab, DispatchReprintRecord } from '@/types/invoice';
import type { TabItem } from '@/types';
import { parseTimestamp } from '@/utils/dateFormat';

interface DispatchReprintRow {
  id: string;
  organization_id: string;
  tab_id: string;
  print_data: DispatchPrintTab;
  created_at: string;
}

const mapPrintData = (raw: DispatchPrintTab): DispatchPrintTab => ({
  ...raw,
  createdAt: parseTimestamp(raw.createdAt) ?? new Date(),
  items: (raw.items ?? []).map((item: TabItem) => ({
    ...item,
    createdAt: parseTimestamp(item.createdAt),
  })),
});

const mapRowToDispatchReprint = (row: DispatchReprintRow): DispatchReprintRecord => ({
  id: row.id,
  organizationId: row.organization_id,
  tabId: row.tab_id,
  printData: mapPrintData(row.print_data),
  createdAt: parseTimestamp(row.created_at) ?? new Date(),
});

export const dispatchReprintService = {
  async createRecord(organizationId: string, tabId: string, printData: DispatchPrintTab): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase.from('dispatch_reprints').insert({
      organization_id: organizationId,
      tab_id: tabId,
      print_data: printData,
    });
    if (error) throw error;
  },

  async getForTab(organizationId: string, tabId: string): Promise<DispatchReprintRecord[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('dispatch_reprints')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('tab_id', tabId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return ((data ?? []) as DispatchReprintRow[]).map(mapRowToDispatchReprint);
  },

  async deleteForTab(tabId: string): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase.from('dispatch_reprints').delete().eq('tab_id', tabId);
    if (error) throw error;
  },
};
